import React, { useEffect, useLayoutEffect, useState } from 'react';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { FormProvider, useForm } from 'react-hook-form';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ApiErrorCodeMessages, ApiErrorCodeTypes } from 'types/enums';
import { Link } from 'types/api.types';
import { routes } from 'app/routing/routesConfig';
import { dynamicYupResolver } from 'app/utils/utils';
import { Alert } from 'app/store/Alerts/slice.types';
import { DeleteAccountForm } from './types';
import { DeleteAccountReasonStep } from './DeleteAccountSteps/DeleteAccountReasonStep';
import { DeleteAccountPasswordStep } from './DeleteAccountSteps/DeleteAccountPasswordStep';
import { defaultValues } from './constants';
import { useModalProps } from '../useModalProps';

interface DeleteAccountDialogComponentProps {
  validationSchema: any;
  link: Link | undefined;
  open: boolean;
  onClose: () => void;
  handleMessages: (content: Alert) => void;
  handleMixpanelEvent: (reason: string) => void;
}

export const DeleteAccountDialogComponent: React.FC<DeleteAccountDialogComponentProps> = ({
  link,
  open: openProp,
  validationSchema,
  onClose,
  handleMessages,
  handleMixpanelEvent,
}) => {
  const navigate = useNavigate();
  const [isPasswordStep, setIsPasswordStep] = useState(false);
  const [isOpen, setOpen] = useState(openProp);

  const form = useForm<DeleteAccountForm>({
    defaultValues,
    mode: 'all',
    context: {
      yupValidationSchema: validationSchema,
    },
    resolver: dynamicYupResolver,
  });

  const {
    watch,
    reset,
    setError,
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = form;

  const submitDisabled = isSubmitting || !isValid;
  const isOther = watch('reason')?.value === 'Other';

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSuccessEvent = (reason: string) => {
    handleMessages({
      message: 'You have successfully deleted your account.',
    });
    handleMixpanelEvent(reason);
    handleClose();
    navigate(routes.signOut.path);
  };

  const onSubmit = async (values: DeleteAccountForm) => {
    const reason = isOther
      ? values.otherReason
      : (values.reason?.value as string);

    try {
      if (link) {
        await axios.request({
          url: link.href,
          method: link.method,
          data: { password: values.password },
          withCredentials: true,
        });
        onSuccessEvent(reason);
      }
    } catch (e: any) {
      if (
        e?.response?.data?.error?.errorCode ===
        ApiErrorCodeTypes.BAD_CREDENTIALS
      ) {
        setError('password', {
          message: ApiErrorCodeMessages.BAD_CREDENTIALS,
        });
      } else {
        handleMessages({
          message: 'Failed to delete account, please try again',
        });
      }
    }
  };

  useLayoutEffect(() => {
    setOpen(openProp);
  }, [openProp]);

  useEffect(() => {
    if (openProp) {
      setIsPasswordStep(false);
    }
  }, [openProp]);

  const { modalProps, contentProps } = useModalProps(openProp);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      autoFocus={false}
      size="md"
      {...modalProps}
    >
      <ModalOverlay />
      <ModalContent data-testid="dialog" {...contentProps}>
        <ModalHeader data-testid="dialog-title" title="Delete Account">
          Delete Account
        </ModalHeader>
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <ModalBody overscrollBehavior="none">
              {isPasswordStep ? (
                <DeleteAccountPasswordStep />
              ) : (
                <DeleteAccountReasonStep showOtherTextField={isOther} />
              )}
            </ModalBody>
            <ModalFooter p="16px 16px 8px">
              <Flex
                direction="row"
                flexWrap="wrap-reverse"
                mr={-2}
                sx={{
                  button: {
                    mb: 2,
                    mr: 2,
                  },
                }}
              >
                <Button variant="secondary" onClick={handleClose} flexGrow={1}>
                  Cancel
                </Button>
                {isPasswordStep ? (
                  <Button
                    type="submit"
                    color="M.70"
                    bg="R.80"
                    _hover={{ bg: 'R.80' }}
                    isDisabled={submitDisabled}
                    flexGrow={1}
                  >
                    Delete Account
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    type="button"
                    onClick={() => setIsPasswordStep(true)}
                    flexGrow={1}
                  >
                    Next
                  </Button>
                )}
              </Flex>
            </ModalFooter>
          </form>
        </FormProvider>
      </ModalContent>
    </Modal>
  );
};
